import React, { useEffect, useRef, useState } from 'react';
import {
  Alert,
  Button,
  DatePicker,
  Modal,
  Space,
  Typography,
} from 'antd';
import { useTranslation } from 'react-i18next';
import type {
  OrderSettlementCommandSubmitResultDto,
  OrderSettlementReadinessDto,
} from '../../../dto/orderSettlement';
import { formatCurrencyLocalized } from '../../../utils/currency';
import { formatLocalizedQuantity } from '../../../utils/numberFormatting';
import { formatUnit } from '../../../utils/units';
import { submitOrderSettlementCommand } from '../api/orderSettlementApi';
import { runOrderSettlementCommandMutation } from '../orderSettlementCommandMutation';
import {
  resolveSettlementRequestToken,
  type SettlementRequestToken,
} from '../settlementRequestKey';
import dayjs, { Dayjs } from 'dayjs';
import './OrderSettlementReadyModal.css';

const { Text } = Typography;

interface Props {
  open: boolean;
  orderId: string;
  readiness: OrderSettlementReadinessDto | null;
  canViewFinance: boolean;
  onClose: () => void;
  onFinalize?: (recognizedAt: string, requestKey: string) => Promise<void>;
  /** Refetch every canonical read model after an accepted command or stale basis. */
  onRefetch: () => Promise<void> | void;
  onResult: (result: OrderSettlementCommandSubmitResultDto) => void;
}

interface SubmitNotice {
  type: 'error' | 'warning';
  message: string;
}

function readinessMessage(readiness: OrderSettlementReadinessDto | null, t: (key: string) => string): string | undefined {
  if (readiness?.commandState === 'ADMIN_REQUIRED' || readiness?.commandState === 'SUPERSEDED') {
    return undefined;
  }
  const reason = readiness?.blockedReasons[0];
  if (!reason) return undefined;
  if (reason.code === 'NO_NEW_DELIVERED_QUANTITY') {
    return t('orders:orderDetail.settlementCommandNoTranche');
  }
  return reason.message || undefined;
}

/**
 * A physical-tranche confirmation only. It deliberately contains no manual
 * amount input: the server owns the frozen command and all financial rules.
 */
export const OrderSettlementReadyModal: React.FC<Props> = ({
  open,
  orderId,
  readiness,
  canViewFinance,
  onClose,
  onFinalize,
  onRefetch,
  onResult,
}) => {
  const { t, i18n } = useTranslation(['orders', 'common']);
  const [submitting, setSubmitting] = useState(false);
  const [recognitionMonth, setRecognitionMonth] = useState<Dayjs | null>(null);
  const [submitNotice, setSubmitNotice] = useState<SubmitNotice | null>(null);
  const retryKey = useRef<SettlementRequestToken | null>(null);
  const inFlight = useRef(false);
  const periodInitialized = useRef(false);

  useEffect(() => {
    if (!open) return;
    retryKey.current = null;
    periodInitialized.current = false;
    setRecognitionMonth(null);
    inFlight.current = false;
    setSubmitNotice(null);
  }, [open, orderId]);

  useEffect(() => {
    if (!open || !readiness || periodInitialized.current) return;
    const dates = readiness.basisTransports.map(item => item.actualDelivery)
      .filter((date): date is string => Boolean(date && dayjs(date).isValid())).sort();
    setRecognitionMonth(dayjs(dates[dates.length - 1] || undefined).startOf('month'));
    periodInitialized.current = true;
  }, [open, orderId, readiness]);

  const quantity = (value: number, unit: string | null) => (
    `${formatLocalizedQuantity(value, i18n.language)} ${formatUnit(unit, i18n.language)}`
  );

  const usesFinancialPreview = canViewFinance;
  const commandVersion = usesFinancialPreview
    ? readiness?.financialPreviewVersion
    : readiness?.physicalVersion;
  const commandBlocked = readiness?.commandState === 'ADMIN_REQUIRED'
    || readiness?.commandState === 'SUPERSEDED';
  const canSubmit = Boolean(
    (onFinalize || (readiness?.canSubmit && readiness.actions.submit))
      && (onFinalize || !usesFinancialPreview || !(readiness?.financialBlockedReasons || []).length)
      && recognitionMonth?.isValid()
      && commandVersion
      && (onFinalize || !commandBlocked),
  );

  const submit = async () => {
    if (!canSubmit || !readiness || submitting || inFlight.current) return;

    const signature = JSON.stringify({
      scope: usesFinancialPreview ? 'FINANCIAL' : 'PHYSICAL',
      version: commandVersion,
      recognitionMonth: recognitionMonth!.format('YYYY-MM'),
      finalize: Boolean(onFinalize),
    });
    retryKey.current = resolveSettlementRequestToken(retryKey.current, signature);

    const command = usesFinancialPreview
      ? {
        requestKey: retryKey.current.key,
        recognitionMonth: recognitionMonth!.format('YYYY-MM'),
        physicalVersion: readiness.physicalVersion,
        financialPreviewVersion: commandVersion!,
      }
      : { requestKey: retryKey.current.key, recognitionMonth: recognitionMonth!.format('YYYY-MM'), physicalVersion: commandVersion! };

    inFlight.current = true;
    setSubmitting(true);
    setSubmitNotice(null);
    try {
      if (onFinalize) {
        await onFinalize(recognitionMonth!.format('YYYY-MM-01'), retryKey.current.key);
        await onRefetch();
        onClose();
        return;
      }
      const result = await runOrderSettlementCommandMutation(
        () => submitOrderSettlementCommand(orderId, command),
        onRefetch,
      );
      if (!result.state) {
        throw new Error('Invalid settlement command response');
      }
      onResult(result);
      onClose();
    } catch (error: any) {
      if (error?.response?.status === 409) {
        retryKey.current = null;
        await onRefetch();
        setSubmitNotice({
          type: 'warning',
          message: t(error?.response?.data?.code === 'SETTLEMENT_PERIOD_CONFLICT'
            ? 'orders:orderDetail.resultPeriodConflict' : 'orders:orderDetail.settlementCommandStale'),
        });
      } else {
        setSubmitNotice({
          type: 'error',
          message: t('orders:orderDetail.settlementCommandSubmitError'),
        });
      }
    } finally {
      inFlight.current = false;
      setSubmitting(false);
    }
  };

  const financialPreview = usesFinancialPreview ? readiness?.financialPreview : undefined;
  const blockedReason = readinessMessage(readiness, t);

  const footer = (
    <Space>
      <Button onClick={onClose}>{t('common:cancel')}</Button>
      <Button
        type="primary"
        disabled={!canSubmit}
        loading={submitting}
        onClick={() => void submit()}
      >
        {t('orders:orderDetail.resultConfirmMonth', { month: recognitionMonth?.format('MM.YYYY') || '—' })}
      </Button>
    </Space>
  );

  return (
    <Modal
      open={open}
      title={t('orders:orderDetail.settlementCommandSubmit')}
      onCancel={onClose}
      footer={footer}
      destroyOnHidden
    >
      <Alert
        type="info"
        showIcon
        message={t('orders:orderDetail.settlementCommandDescription')}
        className="order-settlement-ready-alert"
      />
      {!onFinalize && readiness?.commandState === 'ADMIN_REQUIRED' && (
        <Alert
          type="warning"
          showIcon
          message={t('orders:orderDetail.settlementCommandAdminRequired')}
          description={readiness.adminTask?.categories?.length
            ? `${t('orders:orderDetail.settlementCommandAdminRequiredDescription')} ${t('orders:orderDetail.settlementCommandMissingData')}: ${readiness.adminTask.categories.map((category) => category.label).join('; ')}.`
            : t('orders:orderDetail.settlementCommandAdminRequiredDescription')}
          className="order-settlement-ready-alert"
        />
      )}
      {!onFinalize && readiness?.commandState === 'SUPERSEDED' && (
        <Alert
          type="info"
          showIcon
          message={t('orders:orderDetail.settlementCommandSuperseded')}
          className="order-settlement-ready-alert"
        />
      )}
      {!onFinalize && blockedReason && (
        <Alert type="info" showIcon message={blockedReason} className="order-settlement-ready-alert" />
      )}
      {submitNotice && (
        <Alert
          type={submitNotice.type}
          showIcon
          message={submitNotice.message}
          className="order-settlement-ready-alert"
        />
      )}

      <div style={{ marginBlock: 16 }}>
        <label htmlFor="result-recognition-month">{t('orders:orderDetail.resultRecognitionMonth')}</label>
        <DatePicker
          id="result-recognition-month"
          aria-label={t('orders:orderDetail.resultRecognitionMonth')}
          picker="month"
          format="MM.YYYY"
          value={recognitionMonth}
          onChange={setRecognitionMonth}
          disabled={submitting}
          style={{ display: 'block', width: '100%', marginTop: 8 }}
        />
        <Text type="secondary">{t('orders:orderDetail.resultRecognitionMonthHint')}</Text>
      </div>
      {usesFinancialPreview && !onFinalize && readiness?.financialBlockedReasons?.map(reason => (
        <Alert key={reason.code + reason.message} type="warning" showIcon message={reason.message} />
      ))}
      <Text strong>{t('orders:orderDetail.settlementBasis')}</Text>
      <div className="order-settlement-ready-lines">
        {readiness?.basisTransports.map((transport) => (
          <div key={transport.transportId}>
            <strong>{transport.routeNumber}</strong>
            <Text type="secondary">
              {' · '}{transport.actualDelivery || t('orders:orderDetail.settlementNoDeliveryDate')}
            </Text>
            {transport.cargo.map((cargo) => (
              <div key={`${cargo.orderProductId}-${cargo.unit || 'unit'}`}>
                {t('orders:orderDetail.settlementDelivered')}: {quantity(cargo.deliveredQuantity, cargo.unit)}
                {' · '}
                {t('orders:orderDetail.settlementReady')}: {quantity(cargo.readyQuantity, cargo.unit)}
              </div>
            ))}
          </div>
        ))}
      </div>

      <div className="order-settlement-ready-lines">
        {readiness?.lines.map((line) => (
          <div key={line.orderProductId}>
            <strong>{line.productName}</strong>
            <span>
              {t('orders:orderDetail.settlementOrdered')}: {quantity(line.orderedQuantity, line.unit)}
              {' · '}
              {t('orders:orderDetail.settlementDelivered')}: {quantity(line.deliveredQuantity, line.unit)}
              {' · '}
              {t('orders:orderDetail.settlementReady')}: {quantity(line.readyQuantity, line.unit)}
            </span>
          </div>
        ))}
      </div>

      {financialPreview && (
        <Alert
          type="info"
          className="order-settlement-ready-financial-preview"
          message={t('orders:orderDetail.settlementAutomaticPreview', {
            revenue: formatCurrencyLocalized(financialPreview.revenue, 'PLN', i18n.language),
            totalCost: formatCurrencyLocalized(financialPreview.totalCost, 'PLN', i18n.language),
            margin: formatCurrencyLocalized(financialPreview.margin, 'PLN', i18n.language),
          })}
        />
      )}
    </Modal>
  );
};
